import React, { useState, useRef, useEffect } from 'react';
import './App.css';
import { Search, MessageCircle, FileText, Upload, Sparkles } from 'lucide-react';
import SimpleMarkdownRenderer from './SimpleMarkdownRenderer';

// API Configuration
const API_BASE_URL = 'https://pharma-mcp.hackers.build';

function App() {
  const [activeTab, setActiveTab] = useState('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [labFile, setLabFile] = useState(null);
  const [labResults, setLabResults] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [recentConversations, setRecentConversations] = useState([]);
  const chatMessagesRef = useRef(null);
  const [showScrollButton, setShowScrollButton] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setLabFile(file);
    } else {
      alert('Please select a valid PDF file.');
      setLabFile(null);
    }
  };

  const handleFileUploadClick = () => {
    document.getElementById('file-input').click();
  };

  const stripMarkdown = (text) => {
    return text
      .replace(/#{1,6}\s+/g, '') // Remove headers
      .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold
      .replace(/\*(.*?)\*/g, '$1') // Remove italic
      .replace(/`(.*?)`/g, '$1') // Remove inline code
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Remove links, keep text
      .replace(/\n+/g, ' ') // Replace newlines with spaces
      .trim();
  };

  const saveConversation = (messages) => {
    if (messages.length > 0) {
      const conversation = {
        id: Date.now(),
        title: stripMarkdown(messages[0].content).substring(0, 50) + (messages[0].content.length > 50 ? '...' : ''),
        messages: messages,
        timestamp: new Date().toLocaleString(),
        preview: stripMarkdown(messages[messages.length - 1]?.content || '').substring(0, 100) + '...'
      };
      
      setRecentConversations(prev => {
        const updated = [conversation, ...prev.filter(conv => conv.id !== conversation.id)];
        return updated.slice(0, 10); // Keep only last 10 conversations
      });
    }
  };

  const loadConversation = (conversation) => {
    setChatMessages(conversation.messages);
    setSidebarOpen(false);
  };

  const clearChat = () => {
    if (chatMessages.length > 0) {
      saveConversation(chatMessages);
    }
    setChatMessages([]);
  };

  // Auto-scroll to bottom when new messages are added
  const scrollToBottom = () => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  };

  // Handle scroll events to show/hide scroll button
  const handleScroll = () => {
    if (chatMessagesRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatMessagesRef.current;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      setShowScrollButton(!isNearBottom);
    }
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  // Scroll to bottom when loading state changes (for AI responses)
  useEffect(() => {
    if (loading) {
      scrollToBottom();
    }
  }, [loading]);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type === 'application/pdf') {
        setLabFile(file);
      } else {
        alert('Please select a valid PDF file.');
        setLabFile(null);
      }
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/docs/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          needles: searchQuery.split(' ').filter(term => term.length > 0),
          limit: 10
        })
      });
      
      const data = await response.json();
      setSearchResults(data.docs || []);
    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChat = async (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;

    const userMessage = { role: 'user', content: chatInput };
    setChatMessages(prev => [...prev, userMessage]);
    setChatInput('');
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: chatInput })
      });

      const data = await response.json();
      const aiMessage = {
        role: 'assistant',
        content: data.answer,
        sources: data.sources
      };
      
      setChatMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Chat failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLabUpload = async (e) => {
    e.preventDefault();
    if (!labFile) return;

    const formData = new FormData();
    formData.append('pdf', labFile);

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/lab/analyze`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();
      setLabResults(data.data?.analysis || null);
    } catch (error) {
      console.error('Lab analysis failed:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app">
      {/* Floating particles for special effect */}
      <div className="floating-particles">
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
      </div>
      
      <header className="header">
        <div className="header-content">
          <div className="logo">
            <Sparkles className="logo-icon" />
            <h1>AIPhar</h1>
          </div>
          <p className="tagline">AI-Powered Medical Information Platform</p>
        </div>
      </header>

      <main className="main">
        <div className="container">
          <div className="tabs">
            <button 
              className={`tab ${activeTab === 'search' ? 'active' : ''}`}
              onClick={() => setActiveTab('search')}
            >
              <Search size={20} />
              Search Documents
            </button>
            <button 
              className={`tab ${activeTab === 'chat' ? 'active' : ''}`}
              onClick={() => setActiveTab('chat')}
            >
              <MessageCircle size={20} />
              AI Chat
            </button>
            <button 
              className={`tab ${activeTab === 'lab' ? 'active' : ''}`}
              onClick={() => setActiveTab('lab')}
            >
              <FileText size={20} />
              Lab Analysis
            </button>
          </div>

          <div className="content">
            {activeTab === 'search' && (
              <div className="search-section">
                <form onSubmit={handleSearch} className="search-form">
                  <div className="search-input-container">
                    <Search className="search-icon" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search medical documents, medications, side effects..."
                      className="search-input"
                    />
                    <button type="submit" disabled={loading} className="search-button">
                      {loading ? 'Searching...' : 'Search'}
                    </button>
                  </div>
                </form>

                <div className="results">
                  {searchResults.map((doc, index) => (
                    <div key={doc.id || index} className="result-card">
                      <div className="result-score">
                        Relevance: {(doc.score * 100).toFixed(1)}%
                      </div>
                      <div className="result-content">
                        {doc.contents?.substring(0, 300)}...
                      </div>
                      <div className="result-meta">
                        <span>Created: {new Date(doc.created).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'chat' && (
              <div className="chat-section">
                {/* Chat Sidebar */}
                <div className={`chat-sidebar ${sidebarOpen ? 'open' : ''}`}>
                  <div className="sidebar-header">
                    <h3>Recent Messages</h3>
                    <button 
                      className="close-sidebar"
                      onClick={() => setSidebarOpen(false)}
                    >
                      ✕
                    </button>
                  </div>
                  
                  <div className="sidebar-actions">
                    <button 
                      className="new-chat-btn"
                      onClick={clearChat}
                    >
                      + New Chat
                    </button>
                  </div>
                  
                  <div className="conversations-list">
                    {recentConversations.map((conversation) => (
                      <div 
                        key={conversation.id}
                        className="conversation-item"
                        onClick={() => loadConversation(conversation)}
                      >
                        <div className="conversation-title">{conversation.title}</div>
                        <div className="conversation-preview">{conversation.preview}</div>
                        <div className="conversation-time">{conversation.timestamp}</div>
                      </div>
                    ))}
                    {recentConversations.length === 0 && (
                      <div className="no-conversations">
                        No recent conversations
                      </div>
                    )}
                  </div>
                </div>

                {/* Chat Main Area */}
                <div className="chat-main">
                  <div className="chat-header">
                    <button 
                      className="sidebar-toggle"
                      onClick={() => setSidebarOpen(!sidebarOpen)}
                    >
                      ☰
                    </button>
                    <h3>AI Medical Assistant</h3>
                    <button 
                      className="clear-chat-btn"
                      onClick={clearChat}
                      disabled={chatMessages.length === 0}
                    >
                      Clear
                    </button>
                  </div>

                  <div className="chat-messages" ref={chatMessagesRef} onScroll={handleScroll}>
                    {chatMessages.map((msg, index) => (
                      <div key={index} className={`message ${msg.role}`}>
                        <div className="message-content">
                          {msg.role === 'assistant' ? (
                            <SimpleMarkdownRenderer content={msg.content} />
                          ) : (
                            msg.content
                          )}
                        </div>
                        {msg.sources && (
                          <div className="message-sources">
                            <small>Sources: {msg.sources.length} documents</small>
                          </div>
                        )}
                      </div>
                    ))}
                    {loading && (
                      <div className="message assistant">
                        <div className="message-content">
                          <div className="loading-dots">
                            <span></span>
                            <span></span>
                            <span></span>
                          </div>
                          AI is thinking...
                        </div>
                      </div>
                    )}
                    
                    {/* Scroll to bottom button */}
                    {showScrollButton && (
                      <button 
                        className="scroll-to-bottom-btn"
                        onClick={scrollToBottom}
                        title="Scroll to bottom"
                      >
                        ↓
                      </button>
                    )}
                  </div>

                  <form onSubmit={handleChat} className="chat-form">
                    <div className="chat-input-container">
                      <input
                        type="text"
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        placeholder="Ask about medications, side effects, interactions..."
                        className="chat-input"
                        disabled={loading}
                      />
                      <button type="submit" disabled={loading || !chatInput.trim()} className="chat-button">
                        Send
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {activeTab === 'lab' && (
              <div className="lab-section">
                <form onSubmit={handleLabUpload} className="lab-form">
                  <div 
                    className="file-upload" 
                    onClick={handleFileUploadClick}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                  >
                    <Upload className="upload-icon" />
                    <input
                      type="file"
                      id="file-input"
                      accept=".pdf"
                      onChange={handleFileChange}
                      className="file-input"
                    />
                    <div className="file-label">
                      {labFile ? (
                        <div>
                          <div className="file-name">📄 {labFile.name}</div>
                          <div className="file-size">({(labFile.size / 1024 / 1024).toFixed(2)} MB)</div>
                          <button 
                            type="button"
                            className="remove-file-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setLabFile(null);
                              document.getElementById('file-input').value = '';
                            }}
                          >
                            ✕ Remove
                          </button>
                        </div>
                      ) : (
                        'Choose PDF file or drag & drop here'
                      )}
                    </div>
                  </div>
                  <button 
                    type="submit" 
                    disabled={!labFile || loading} 
                    className="analyze-button"
                  >
                    {loading ? 'Analyzing...' : 'Analyze Lab Results'}
                  </button>
                </form>

                {labResults && (
                  <div className="lab-results">
                    <h3>Analysis Summary</h3>
                    <p>{labResults.summary}</p>

                    {labResults.criticalFindings?.length > 0 && (
                      <div className="critical-alerts">
                        <h4>⚠️ Requires Attention</h4>
                        {labResults.criticalFindings.map((finding, i) => (
                          <div key={i} className="alert">{finding}</div>
                        ))}
                      </div>
                    )}

                    <h4>Test Results</h4>
                    <div className="test-results">
                      {labResults.tests?.map((test, i) => (
                        <div key={i} className={`test-result ${test.status}`}>
                          <div className="test-name">{test.testName}</div>
                          <div className="test-value">
                            {test.value} {test.unit}
                            <span className="test-range">({test.referenceRange})</span>
                          </div>
                          <div className={`test-status ${test.status}`}>
                            {test.status.toUpperCase()}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default App;
